import { Inbox, RotateCw } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { Link, useParams } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import useTranslations from "@/shared/lang/useTranslations";

import { OrderOutcome } from "../components/order/OrderOutcome";
import { OrderSummaryCard } from "../components/order/OrderSummaryCard";
import { OrderTimeline } from "../components/order/OrderTimeline";
import { CryptoSwapStatusPill } from "../components/orders/CryptoSwapStatusPill";
import { MonoValue } from "../components/shared/CryptoSwapBits";
import { useAmountFormatter } from "../hooks/useAmountFormatter";
import { useCryptoSwapLabels } from "../hooks/useCryptoSwapLabels";
import { useCryptoSwapOrder } from "../hooks/useCryptoSwapOrder";
import { shortenAddress } from "../utils/cryptoSwapAddress";
import { CRYPTO_SWAPS_HISTORY_PATH, CRYPTO_SWAPS_PATH } from "../utils/cryptoSwapLinks";
import { bucketForStatus, isNonTerminalStatus } from "../utils/cryptoSwapStatus";

/** `/app/crypto-swaps/orders/:orderId` — one order, polled while the USDT is on its way. */
export default function CryptoSwapOrderPage() {
  const { orderId = null } = useParams<{ orderId: string }>();
  const { t, activeLanguage } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const format = useAmountFormatter();
  const { order, isLoading, isStale, notFound, lastUpdatedAt, refresh } = useCryptoSwapOrder(orderId);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useAppShellBreadcrumb(
    useMemo(
      () => [
        { label: t("page_breadcrumb_wallets"), to: "/app/wallets" },
        { label: t("page_title"), to: CRYPTO_SWAPS_PATH },
        { label: t("history_title"), to: CRYPTO_SWAPS_HISTORY_PATH },
        { label: t("order_breadcrumb") },
      ],
      [t],
    ),
  );

  // The submit button was unmounted by the navigation; move focus once per order, never on a poll.
  const focusedOrderIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!order || focusedOrderIdRef.current === order.id) return;
    focusedOrderIdRef.current = order.id;
    headingRef.current?.focus();
  }, [order]);

  if (isLoading && !order) {
    return (
      <div className="flex flex-col gap-6">
        <ColocaSkeleton className="h-28 rounded-2xl" />
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          <ColocaSkeleton className="h-96 rounded-3xl" />
          <ColocaSkeleton className="h-72 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (notFound || !order) {
    return (
      <ColocaEmpty
        icon={Inbox}
        title={t("order_not_found_title")}
        description={t("order_not_found_description")}
      >
        <ColocaButton variant="primary" render={<Link to={CRYPTO_SWAPS_HISTORY_PATH} />}>
          {t("order_back_to_history")}
        </ColocaButton>
      </ColocaEmpty>
    );
  }

  const bucket = bucketForStatus(order.status);
  const isLive = isNonTerminalStatus(order.status);
  const amount = `${format(order.receive.amount, order.receive.currency)} ${order.receive.currency}`;
  const headline =
    bucket === "done"
      ? t("order_headline_paid", { amount })
      : bucket === "failed"
        ? t("order_headline_failed", { amount })
        : t("order_headline_live", { amount });

  return (
    <div className="flex flex-col gap-6 pb-16">
      <header className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex min-w-0 flex-col gap-3">
          <CryptoSwapStatusPill status={order.status} />
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="rounded-sm text-3xl leading-tight font-bold tracking-tight text-foreground focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring md:text-4xl"
          >
            {headline}
          </h1>
          <p className="max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
            {t("order_subtitle", {
              address: shortenAddress(order.destination.address) || "—",
              network: labels.networkShort(order.destination.network),
            })}
          </p>
        </div>

        {isLive && (
          <div className="flex shrink-0 items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3">
            <span className="relative flex size-2.5">
              <span
                aria-hidden="true"
                className="absolute -inset-1 animate-pulse rounded-full bg-success/30"
              />
              <span aria-hidden="true" className="absolute inset-0 rounded-full bg-success" />
            </span>
            <span className="flex flex-col">
              <span className="text-[13px] font-semibold">{t("order_live_title")}</span>
              <MonoValue className="text-xs text-muted-foreground">
                {lastUpdatedAt
                  ? t("order_live_updated", { time: lastUpdatedAt.toLocaleTimeString(activeLanguage) })
                  : "—"}
              </MonoValue>
            </span>
          </div>
        )}
      </header>

      {isStale && (
        <ColocaAlert
          variant="secondary"
          text={t("order_live_stale")}
          actions={
            <ColocaButton variant="default" icon={RotateCw} onClick={refresh}>
              {t("history_refresh")}
            </ColocaButton>
          }
        />
      )}

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="flex flex-col gap-4">
          <OrderOutcome order={order} />
          <OrderTimeline order={order} />
        </div>
        <OrderSummaryCard order={order} />
      </div>
    </div>
  );
}
